import { ReactNode, useContext } from 'react';
import { Button } from '@pelckmans/business-components/components/button';
import { Dialog, ModalDialog } from '@pelckmans/business-components/components/dialog';
import JWPlayer, { JWPlayerConfig } from '@jwplayer/jwplayer-react';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../../contexts/ModalContext';
import MaterialModalHeader from './common/MaterialModalHeader';
import { Node } from '../_api/TableOfContentsApi';

type JWPlayerProps = {
  id: string;
  // {playlist: string} is used for converted video/audio playback on jwplayer, {file: string} is used for AWS S3 hosted media
  media: { playlist: string } | { file: string };
  downloadUrl?: string;
  name: string;
  node: Node;
  contentType: ReactNode;
  audioOnly?: boolean;
  downloadDisclaimer?: JSX.Element;
  tutorialId?: string;
};

export default function JWPlayerModal({
  id,
  media,
  downloadUrl,
  name,
  node,
  contentType,
  audioOnly,
  downloadDisclaimer,
  tutorialId,
}: JWPlayerProps) {
  const { t } = useTranslation();

  const { closeModal } = useContext(ModalContext);

  const handleDownload = () => {
    window.open(downloadUrl, '_blank');
  };

  const config: JWPlayerConfig = {
    displaydescription: false,
    displaytitle: false,
    autostart: true,
    ...(audioOnly
      ? {
          width: 'auto',
          height: 40,
          logo: undefined,
          primary: 'html5',
        }
      : {}),
  };

  return (
    <ModalDialog
      id={id}
      closeViaEscape={!tutorialId}
      closeOnClickOutside={!tutorialId}
      closeTitle={t('ACTIONS.CLOSE')}
      onClose={() => closeModal(id)}
      className="material-dialog"
    >
      <MaterialModalHeader
        title={name}
        description={node.description}
        contentType={contentType}
        tutorialId={tutorialId}
      />
      <Dialog.Body>
        <style>{`#${id} .bc-dialog__body { padding: 0;}`}</style>
        <JWPlayer library="https://content.jwplatform.com/libraries/zyraJCSg.js" config={config} {...media} />
      </Dialog.Body>
      {(downloadUrl || downloadDisclaimer) && (
        <Dialog.Footer className={downloadDisclaimer ? 'justify-content-start' : undefined}>
          {downloadDisclaimer ?? (
            <Button onClick={handleDownload} variant="primary">
              <i className="bc-icon bc-icon-arrow-down bc-icon--left" />
              <span>{t('ACTIONS.DOWNLOAD')}</span>
            </Button>
          )}
        </Dialog.Footer>
      )}
    </ModalDialog>
  );
}
